import { z } from "zod";
import { Id, IsoDateTime } from "./common";
import { AlertSeverity, AlertStatus } from "./alert";
import { MaintenanceState } from "./asset";
import { LocationConfidence } from "./facility";
import {
  BoundaryState,
  MapAssetMarker,
  MapSite,
  OnlineState,
  RiskLevel,
  SiteBoundary,
} from "./state";
import { WorkItemPriority, WorkItemStatus } from "./work-item";

export const WeatherContextResponse = z.object({
  siteId: Id,
  tenantId: Id,
  provider: z.literal("Google"),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  observedAt: IsoDateTime,
  fetchedAt: IsoDateTime,
  expiresAt: IsoDateTime,
  summary: z.string(),
  riskLevel: RiskLevel,
  riskReasons: z.array(z.string()),
  fromCache: z.boolean(),
  /** Ambient temperature at the site in degrees Celsius. Null if unavailable. */
  temperatureCelsius: z.number().nullable().optional(),
  /** Wind speed in metres per second. Null if unavailable. */
  windSpeedMps: z.number().nullable().optional(),
  /** Relative humidity percentage (0–100). Null if unavailable. */
  humidity: z.number().nullable().optional(),
});
export type WeatherContextResponse = z.infer<typeof WeatherContextResponse>;

export const OverlayContextStatus = z.enum(["Available", "NotSupported", "Unavailable"]);
export type OverlayContextStatus = z.infer<typeof OverlayContextStatus>;

export const NwsWeatherPeriod = z.object({
  name: z.string(),
  startTime: IsoDateTime.nullable(),
  endTime: IsoDateTime.nullable(),
  temperature: z.number().nullable(),
  temperatureUnit: z.string().nullable(),
  windSpeed: z.string().nullable(),
  windDirection: z.string().nullable(),
  shortForecast: z.string().nullable(),
  detailedForecast: z.string().nullable(),
});
export type NwsWeatherPeriod = z.infer<typeof NwsWeatherPeriod>;

/** GeoJSON Polygon / MultiPolygon in lng/lat order (NWS active alerts). */
const NwsGeoPosition = z.tuple([z.number(), z.number()]);
const NwsGeoLinearRing = z.array(NwsGeoPosition).min(3);
const NwsGeoPolygonCoordinates = z.array(NwsGeoLinearRing).min(1);
const NwsGeoMultiPolygonCoordinates = z.array(NwsGeoPolygonCoordinates).min(1);

export const NwsAlertGeometry = z.union([
  z.object({
    type: z.literal("Polygon"),
    coordinates: NwsGeoPolygonCoordinates,
  }),
  z.object({
    type: z.literal("MultiPolygon"),
    coordinates: NwsGeoMultiPolygonCoordinates,
  }),
]);
export type NwsAlertGeometry = z.infer<typeof NwsAlertGeometry>;

export const NwsWeatherAlert = z.object({
  id: z.string(),
  event: z.string(),
  severity: z.string().nullable(),
  urgency: z.string().nullable(),
  certainty: z.string().nullable(),
  headline: z.string().nullable(),
  areaDesc: z.string().nullable(),
  effective: IsoDateTime.nullable(),
  expires: IsoDateTime.nullable(),
  url: z.string().url().nullable(),
  /** Populated when NWS returns polygon geometry for the alert. */
  geometry: NwsAlertGeometry.nullable().optional(),
});
export type NwsWeatherAlert = z.infer<typeof NwsWeatherAlert>;

export const NwsWeatherOverlayResponse = z.object({
  siteId: Id,
  tenantId: Id,
  provider: z.literal("NOAA_NWS"),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  status: OverlayContextStatus,
  reason: z.string().nullable(),
  summary: z.string().nullable(),
  office: z.string().nullable(),
  gridX: z.number().int().nullable(),
  gridY: z.number().int().nullable(),
  alerts: z.array(NwsWeatherAlert),
  forecastPeriods: z.array(NwsWeatherPeriod),
  fetchedAt: IsoDateTime,
  expiresAt: IsoDateTime,
  fromCache: z.boolean(),
});
export type NwsWeatherOverlayResponse = z.infer<typeof NwsWeatherOverlayResponse>;

export const EarthquakeOverlayFeature = z.object({
  id: z.string(),
  title: z.string(),
  magnitude: z.number().nullable(),
  place: z.string().nullable(),
  time: IsoDateTime.nullable(),
  updated: IsoDateTime.nullable(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  depthKm: z.number().nullable(),
  url: z.string().url().nullable(),
});
export type EarthquakeOverlayFeature = z.infer<typeof EarthquakeOverlayFeature>;

export const EarthquakeOverlayResponse = z.object({
  siteId: Id,
  tenantId: Id,
  provider: z.literal("USGS"),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  status: OverlayContextStatus,
  reason: z.string().nullable(),
  minLatitude: z.number().min(-90).max(90),
  maxLatitude: z.number().min(-90).max(90),
  minLongitude: z.number().min(-180).max(180),
  maxLongitude: z.number().min(-180).max(180),
  minMagnitude: z.number(),
  startTime: IsoDateTime,
  endTime: IsoDateTime,
  features: z.array(EarthquakeOverlayFeature),
  fetchedAt: IsoDateTime,
  expiresAt: IsoDateTime,
  fromCache: z.boolean(),
});
export type EarthquakeOverlayResponse = z.infer<typeof EarthquakeOverlayResponse>;

export const GeocodeAddressInput = z.object({
  addressLine1: z.string().max(200).nullable().optional(),
  addressLine2: z.string().max(200).nullable().optional(),
  city: z.string().max(120).nullable().optional(),
  stateRegion: z.string().max(120).nullable().optional(),
  postalCode: z.string().max(40).nullable().optional(),
  country: z.string().max(2).nullable().optional(),
});
export type GeocodeAddressInput = z.infer<typeof GeocodeAddressInput>;

export const GeocodeResult = z.object({
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  formattedAddress: z.string(),
  locationConfidence: LocationConfidence,
  fromCache: z.boolean(),
});
export type GeocodeResult = z.infer<typeof GeocodeResult>;

export const MapAssetsFilter = z.object({
  siteId: Id.optional(),
  buildingId: Id.optional(),
  floorId: Id.optional(),
  riskLevel: RiskLevel.optional(),
  onlineState: OnlineState.optional(),
  maintenanceState: MaintenanceState.optional(),
  boundaryState: BoundaryState.optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  cursor: z.string().optional(),
});
export type MapAssetsFilter = z.infer<typeof MapAssetsFilter>;

export const MapAlertMarker = z.object({
  alertId: Id,
  title: z.string(),
  severity: AlertSeverity,
  status: AlertStatus,
  siteId: Id.nullable(),
  assetId: Id.nullable(),
  deviceId: Id.nullable(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type MapAlertMarker = z.infer<typeof MapAlertMarker>;

export const MapWorkItemMarker = z.object({
  workItemId: Id,
  title: z.string(),
  priority: WorkItemPriority,
  status: WorkItemStatus,
  siteId: Id.nullable(),
  assetId: Id.nullable(),
  deviceId: Id.nullable(),
  alertId: Id.nullable(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  createdAt: IsoDateTime,
  updatedAt: IsoDateTime,
});
export type MapWorkItemMarker = z.infer<typeof MapWorkItemMarker>;

export const MapAlertsFilter = z.object({
  status: AlertStatus.optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  cursor: z.string().optional(),
});
export type MapAlertsFilter = z.infer<typeof MapAlertsFilter>;

export const MapWorkItemsFilter = z.object({
  status: WorkItemStatus.optional(),
  limit: z.coerce.number().int().min(1).max(500).optional(),
  cursor: z.string().optional(),
});
export type MapWorkItemsFilter = z.infer<typeof MapWorkItemsFilter>;

// ---------------------------------------------------------------------------
// Places-assisted site setup (PLACES_SITE_SETUP feature gate)
// ---------------------------------------------------------------------------

export const PlacesSearchInput = z.object({
  query: z.string().min(2).max(200),
  maxResults: z.coerce.number().int().min(1).max(10).optional().default(5),
});
export type PlacesSearchInput = z.infer<typeof PlacesSearchInput>;

export const PlaceSuggestion = z.object({
  placeId: z.string(),
  name: z.string(),
  formattedAddress: z.string(),
  latitude: z.number().min(-90).max(90),
  longitude: z.number().min(-180).max(180),
  locationConfidence: LocationConfidence,
});
export type PlaceSuggestion = z.infer<typeof PlaceSuggestion>;

export const PlacesSearchResult = z.object({
  items: z.array(PlaceSuggestion),
  query: z.string(),
});
export type PlacesSearchResult = z.infer<typeof PlacesSearchResult>;

// ---------------------------------------------------------------------------
// Landside routing / ETA (ROUTES_ETA feature gate)
// ---------------------------------------------------------------------------

export const RoutesEtaInput = z.object({
  /** Latitude of the origin (e.g. vehicle or depot location). */
  originLat: z.number().min(-90).max(90),
  /** Longitude of the origin. */
  originLng: z.number().min(-180).max(180),
});
export type RoutesEtaInput = z.infer<typeof RoutesEtaInput>;

export const RoutesEtaResult = z.object({
  siteId: Id,
  tenantId: Id,
  originLat: z.number(),
  originLng: z.number(),
  destinationLat: z.number(),
  destinationLng: z.number(),
  distanceMeters: z.number().int(),
  durationSeconds: z.number().int(),
  /** Traffic-aware duration. Equals durationSeconds when real-time traffic is unavailable. */
  trafficDurationSeconds: z.number().int(),
  /** Human-readable distance string from the routing provider. */
  distanceText: z.string(),
  /** Human-readable duration string from the routing provider. */
  durationText: z.string(),
  /** Always present. Reminds callers this is public-road routing only. */
  roadOnlyWarning: z.string(),
  computedAt: IsoDateTime,
});
export type RoutesEtaResult = z.infer<typeof RoutesEtaResult>;

// ---------------------------------------------------------------------------
// Aggregated site model (returned by GET /map/sites/{siteId}/model)
// ---------------------------------------------------------------------------

export const SiteModelResponse = z.object({
  siteId: Id,
  tenantId: Id,
  site: MapSite,
  boundary: SiteBoundary.nullable(),
  assetMarkers: z.array(MapAssetMarker),
  generatedAt: IsoDateTime,
});
export type SiteModelResponse = z.infer<typeof SiteModelResponse>;
